import {
  dashboardEventSchema,
  platformLabel,
  type DashboardEvent,
} from '@midiautolistening/contracts';

import { useAgentsStore } from '@/stores/agents';
import { useCommentsStore } from '@/stores/comments';
import { usePostsStore } from '@/stores/posts';
import { useProjectsStore } from '@/stores/projects';
import { useSelectorPacksStore } from '@/stores/selectorPacks';
import { useUiStore } from '@/stores/ui';

import { ensureAccessToken } from './api';
import { session } from './session';

/**
 * The dashboard realtime socket.
 *
 * Three properties matter here:
 *
 *  1. **Every frame is validated with `dashboardEventSchema`.** A frame that does
 *     not match a known event is dropped and counted, never partially applied.
 *     The server also sends a `connection.ready` handshake frame which is
 *     deliberately outside the union — dropping unknown frames is what lets the
 *     server add events without breaking an older tab that is still open.
 *  2. **The token is refreshed before connecting.** The access token travels in
 *     the handshake query string (browsers cannot set headers on a WebSocket
 *     upgrade), so opening a socket with a nearly-expired token would produce a
 *     reconnect loop.
 *  3. **Reconnection backs off with jitter.** Without jitter every open tab
 *     retries in lockstep after an API restart.
 */

const WS_URL = import.meta.env.VITE_WS_URL || 'ws://localhost:3000/ws';

const INITIAL_BACKOFF_MS = 1_000;

/**
 * How long the retry delay is allowed to grow.
 *
 * The ceiling is lower in development because the outage it is pacing against is
 * different in kind. A dev restart lasts as long as Nest takes to boot, so a 30s
 * ceiling spends most of its time waiting on a server that is already answering,
 * and the disconnected banner outlives the disconnection by a wide margin. In
 * production an outage is real and open-ended, and thirty seconds between
 * attempts is the difference between backing off and hammering.
 */
const MAX_BACKOFF_MS = import.meta.env.DEV ? 5_000 : 30_000;
const BACKOFF_FACTOR = 1.7;

class DashboardSocket {
  private socket: WebSocket | null = null;
  private reconnectTimer: number | null = null;
  private backoffMs = INITIAL_BACKOFF_MS;
  /** Set by `disconnect()` so a deliberate close does not schedule a retry. */
  private closedByUs = false;
  private connecting = false;
  private droppedFrames = 0;

  constructor() {
    // Guarded because this module is instantiated on import, and the store tests
    // import it outside a DOM.
    if (typeof window === 'undefined') return;

    window.addEventListener('online', () => this.retryNow());
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.retryNow();
    });
  }

  async connect(): Promise<void> {
    if (this.connecting) return;
    if (this.socket && this.socket.readyState <= WebSocket.OPEN) return;

    this.closedByUs = false;
    this.connecting = true;

    const ui = useUiStore();
    ui.setRealtimeStatus(this.backoffMs === INITIAL_BACKOFF_MS ? 'connecting' : 'reconnecting');

    const token = await ensureAccessToken();
    this.connecting = false;

    if (!token) {
      // No usable session: stay closed. The router guard is already sending the
      // user to /login.
      ui.setRealtimeStatus('closed');
      return;
    }

    /**
     * Read the kill switch from the server before trusting the socket for it.
     *
     * `emergencyStopActive` was written by exactly one thing — the
     * `emergency_stop.changed` event — and started `false`. So a tab opened, or
     * merely reloaded, while the workspace was halted showed no banner at all:
     * the event had been broadcast before this tab existed and is never replayed.
     * The same hole reopens on every reconnect, where the frames sent while the
     * socket was down are simply gone.
     *
     * Done here rather than in `onopen` so it still runs when the WebSocket
     * cannot be established at all but HTTP is fine — a proxy that blocks the
     * upgrade must not also hide the fact that automation is stopped. Not
     * awaited: the socket has no reason to wait on it, and the read reports its
     * own failure by leaving `emergencyStopKnown` false.
     */
    void ui.syncEmergencyStop();

    const url = new URL(WS_URL);
    url.searchParams.set('token', token);

    let socket: WebSocket;
    try {
      socket = new WebSocket(url.toString());
    } catch {
      this.scheduleReconnect();
      return;
    }

    this.socket = socket;

    socket.onopen = () => {
      this.backoffMs = INITIAL_BACKOFF_MS;
      useUiStore().setRealtimeStatus('open');
    };

    socket.onmessage = (message: MessageEvent<string>) => {
      this.handleFrame(message.data);
    };

    socket.onerror = () => {
      // `onclose` always follows; reconnect scheduling lives there so it happens
      // exactly once per failure.
    };

    socket.onclose = () => {
      this.socket = null;
      if (this.closedByUs) {
        useUiStore().setRealtimeStatus('closed');
        return;
      }
      this.scheduleReconnect();
    };
  }

  disconnect(): void {
    this.closedByUs = true;
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.socket?.close(1000, 'client navigating away');
    this.socket = null;
    this.backoffMs = INITIAL_BACKOFF_MS;
    useUiStore().setRealtimeStatus('idle');
  }

  /**
   * Retry now instead of serving out the rest of the backoff.
   *
   * The backoff exists to keep a fleet of tabs from hammering a server that is
   * down; it is not a rule about how long a person must look at a stale screen. A
   * tab that comes back to the foreground, or a machine that just regained its
   * network, is precisely the moment the delay stops being useful — and it is the
   * moment the user is most likely to notice the banner. So the pending timer is
   * dropped and the delay is reset to its floor.
   *
   * Everything the timer path guards against is re-checked here, because this is
   * driven by the browser rather than by our own failure handling: a deliberate
   * `disconnect()`, a session that is already gone, and a socket that is in fact
   * fine all mean there is nothing to do.
   */
  private retryNow(): void {
    if (this.closedByUs) return;
    if (!session.refreshToken) return;
    if (this.socket && this.socket.readyState <= WebSocket.OPEN) return;

    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    this.backoffMs = INITIAL_BACKOFF_MS;
    void this.connect();
  }

  private scheduleReconnect(): void {
    if (this.closedByUs) return;
    if (this.reconnectTimer !== null) return;
    if (!session.refreshToken) {
      useUiStore().setRealtimeStatus('closed');
      return;
    }

    useUiStore().setRealtimeStatus('reconnecting');

    // ±25% jitter so tabs reopened after an API restart do not stampede.
    const jitter = this.backoffMs * 0.25 * (Math.random() * 2 - 1);
    const delay = Math.max(250, Math.round(this.backoffMs + jitter));
    this.backoffMs = Math.min(MAX_BACKOFF_MS, Math.round(this.backoffMs * BACKOFF_FACTOR));

    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      void this.connect();
    }, delay);
  }

  private handleFrame(raw: string): void {
    let payload: unknown;
    try {
      payload = JSON.parse(raw);
    } catch {
      this.droppedFrames += 1;
      return;
    }

    const parsed = dashboardEventSchema.safeParse(payload);
    if (!parsed.success) {
      // Includes the server's `connection.ready` handshake, which carries no
      // state and is intentionally not part of the event union.
      this.droppedFrames += 1;
      return;
    }

    dispatchDashboardEvent(parsed.data);
  }

  get droppedFrameCount(): number {
    return this.droppedFrames;
  }
}

/**
 * Fan a validated event into the stores.
 *
 * The stores are resolved inside the function, not at module scope: this runs
 * only after the app has mounted, so Pinia is guaranteed to be active, and the
 * module stays importable from anywhere.
 */
export function dispatchDashboardEvent(event: DashboardEvent): void {
  const agents = useAgentsStore();
  const projects = useProjectsStore();
  const posts = usePostsStore();
  const comments = useCommentsStore();
  const selectorPacks = useSelectorPacksStore();
  const ui = useUiStore();

  switch (event.event) {
    case 'agent.status_changed':
      agents.applyAgentStatusChanged(event);
      break;

    case 'browser.status_changed':
      agents.applyBrowserStatusChanged(event);
      if (event.status === 'USER_ACTION_REQUIRED') {
        ui.notifyWarning(
          'Cần bạn xử lý thủ công',
          'Một profile trình duyệt đang chờ bạn tự xử lý checkpoint / 2FA trong cửa sổ Chromium.',
        );
      }
      break;

    case 'platform.session_changed':
      agents.applyPlatformSessionChanged(event);
      if (event.requiresUserAction) {
        ui.notifyWarning(
          `Phiên ${platformLabel(event.platform)} cần bạn can thiệp`,
          'Hệ thống cố ý không tự vượt checkpoint/2FA. Hãy mở cửa sổ Chromium và xử lý.',
        );
      }
      break;

    case 'project.status_changed':
      projects.applyProjectStatusChanged(event);
      agents.applyProjectStatus(event.projectId, event.status);
      break;

    case 'post.ingested':
      posts.applyPostIngested(event);
      projects.incrementPostCount(event.projectId, event.isNew);
      break;

    case 'selector.health_updated':
      selectorPacks.applySelectorHealthUpdated(event);
      break;

    case 'emergency_stop.changed':
      ui.setEmergencyStop(event.active, event.reason, event.changedAt);
      break;

    /**
     * The approval queue reads this. It used to sit in the no-op branch below
     * under "Not surfaced on any MVP screen", which was true when that line was
     * written and stopped being true the day `CommentJobsView` shipped — leaving
     * the one screen with a "Duyệt & gửi" button as the only screen in the app
     * that never heard about a job changing state.
     */
    case 'comment.job_updated':
      comments.applyJobUpdated(event);
      break;

    case 'search.cycle_progress':
      // Drives the "Chu kỳ vừa rồi" line on the project list, live.
      projects.applyCycleProgress(event);
      break;
  }
}

export const dashboardSocket = new DashboardSocket();
